import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { notificationService } from "./services";
import { useAuth } from "./auth-context";

interface NotificationState {
  unreadCount: number;
  refresh: () => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationState | null>(null);

const POLL_INTERVAL_MS = 60_000;

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    try {
      const { unread_count } = await notificationService.count();
      setUnreadCount(unread_count ?? 0);
    } catch {
      /* silent */
    }
  }, [isAuthenticated]);

  const markAllRead = useCallback(async () => {
    if (!isAuthenticated) return;
    await notificationService.markAllRead();
    setUnreadCount(0);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    let id = 0;

    const stop = () => {
      window.clearInterval(id);
      id = 0;
    };

    /*
      Polling only runs while the app is actually on screen.

      A minute-interval request that keeps firing in a backgrounded tab is a
      radio wake-up a minute, every minute, for a number nobody is looking at —
      which on a phone is a real amount of battery, and on a metered connection
      a real amount of data. Coming back to the app refreshes immediately, so
      the count is never stale by the time it is read.
    */
    const start = () => {
      if (id) return;
      id = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refresh();
        start();
      } else {
        stop();
      }
    };

    /*
      `focus` catches what `visibilitychange` does not: a desktop window that
      was never hidden, only behind another one.
    */
    const onFocus = () => void refresh();

    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthenticated, refresh]);

  const value = useMemo(
    () => ({ unreadCount, refresh, markAllRead }),
    [unreadCount, refresh, markAllRead],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationState {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used inside NotificationProvider");
  return ctx;
}
